import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Sparkles, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const MagicLinkPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const verifyMagicLink = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setError('Invalid magic link - no token provided');
        return;
      }

      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API}/api/auth/magic-link/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Failed to verify magic link');
        }

        const result = await response.json();
        setStatus('success');

        // Wait a moment to show success, then redirect
        setTimeout(() => {
          if (result.is_new_user) {
            navigate('/onboarding', { 
              replace: true, 
              state: { user: result.user }
            });
          } else {
            navigate('/app', { 
              replace: true, 
              state: { user: result.user }
            });
          }
        }, 1500);
      } catch (error) {
        console.error('Magic link verification error:', error);
        setStatus('error');
        setError(error.message || 'Failed to verify magic link');
      }
    };

    verifyMagicLink();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen cyber-bg flex items-center justify-center p-4">
      <div className="glass-card rounded-3xl p-8 max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <div className="relative mb-6">
              <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto" />
              <Sparkles className="w-6 h-6 text-[#CCFF00] absolute -top-1 -right-1 animate-pulse" />
            </div>
            <h2 className="font-outfit text-2xl font-bold text-white mb-2">
              Verifying your magic link...
            </h2>
            <p className="text-white/60">
              Just a moment while we sign you in
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mb-6">
              <CheckCircle className="w-16 h-16 text-[#CCFF00] mx-auto animate-bounce" />
            </div>
            <h2 className="font-outfit text-2xl font-bold text-white mb-2">
              You're in! ✨
            </h2>
            <p className="text-white/60">
              Redirecting you now...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mb-6">
              <XCircle className="w-16 h-16 text-red-400 mx-auto" />
            </div>
            <h2 className="font-outfit text-2xl font-bold text-white mb-2">
              Link Invalid or Expired
            </h2>
            <p className="text-white/60 mb-6">
              {error}
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full h-12 rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold glow-button"
              data-testid="back-to-login-button"
            >
              Back to Login
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default MagicLinkPage;
